import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const decodeJwtPayload = (jwtToken) => {
        const parts = String(jwtToken || '').split('.');
        if (parts.length < 2) throw new Error('Invalid JWT');
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
        return JSON.parse(atob(padded));
    };

    useEffect(() => {
        // Request interceptor to add token
        const reqInterceptor = axios.interceptors.request.use(
            config => {
                const token = localStorage.getItem('token');
                if (token) {
                    if (!config.headers) config.headers = {};
                    config.headers['Authorization'] = `Bearer ${token}`;
                }
                return config;
            },
            error => Promise.reject(error)
        );

        // Response interceptor to handle 401
        const resInterceptor = axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(reqInterceptor);
            axios.interceptors.response.eject(resInterceptor);
        };
    }, []);

    useEffect(() => {
        const verifyToken = async () => {
            if (token) {
                // No need to set defaults.headers.common here anymore
                try {
                    const payload = decodeJwtPayload(token);
                    if (payload.exp && payload.exp * 1000 < Date.now()) {
                        throw new Error("Token expired");
                    }
                    setUser({ 
                        username: payload.username, 
                        id: payload.id,
                        role: payload.role,
                        max_hives: payload.max_hives,
                        subscription_end: payload.subscription_end,
                        city: payload.city
                    });
                } catch (e) {
                    console.error("Invalid token:", e);
                    logout();
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        };

        verifyToken();
    }, [token]);

    const login = async (username, password) => {
        try {
            const cleanUsername = String(username ?? '').trim();
            const cleanPassword = String(password ?? '');
            const res = await axios.post('/api/auth/login', { username: cleanUsername, password: cleanPassword });
            const { token, userId, username: resUsername } = res.data;
            localStorage.setItem('token', token);
            setToken(token);
            setUser({ id: userId, username: resUsername });
            return { success: true };
        } catch (err) {
            return { success: false, error: err.response?.data?.error || "Giriş yapılamadı" };
        }
    };

    const register = async (username, password) => {
        // Updated to use the admin route if logged in as admin, but here we keep the public register for now
        // or disable it. Since we are moving to "Admin only creates users", this function might be repurposed
        // or used only by Admin page.
        try {
            // If user is admin, use admin endpoint, otherwise try public (which we will lock)
            const endpoint = user?.username === 'admin' ? '/api/admin/users' : '/api/auth/register';
            await axios.post(endpoint, { username, password });
            return { success: true };
        } catch (err) {
            return { success: false, error: err.response?.data?.error || "Registration failed" };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
    };

    return (
        <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
